import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Upload, Camera } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const METHODS = ['QUANTITY_BASED', 'STAGE_BASED', 'MILESTONE_BASED', 'AI_VISUAL_ESTIMATE', 'MANUAL_REVIEW'];
const MILESTONES = ['NOT_STARTED', 'STARTED', 'COMPLETE'];

// Captures a progress OBSERVATION (a proposal that still needs human review). Method-aware:
// AI needs image(s); STAGE picks a stage; MILESTONE picks a state; MANUAL/QUANTITY submit directly.
const ProgressCaptureModal = ({ projectId, activityId, defaultMethod, onClose, onCreated }) => {
  const [method, setMethod] = useState(defaultMethod || 'MANUAL_REVIEW');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [stageKey, setStageKey] = useState('');
  const [milestoneState, setMilestoneState] = useState('STARTED');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/progress/stage-templates`).then((r) => setTemplates(r.data)).catch(() => {});
  }, [projectId]);

  const stages = templates.find((t) => t.id === templateId)?.stages || [];

  const submit = async () => {
    setBusy(true);
    try {
      if (method === 'AI_VISUAL_ESTIMATE') {
        const fd = new FormData();
        (files || []).forEach((f) => fd.append('files', f));
        fd.append('sourceType', 'IMAGE');
        const r = await axios.post(`${API}/projects/${projectId}/activities/${activityId}/progress/ai-estimate`, fd);
        toast.success(`AI proposal created (${r.data.reviewStatus})`);
      } else {
        const body = { method, sourceType: 'IMAGE' };
        if (method === 'STAGE_BASED') { body.stageTemplateId = templateId; body.stageKey = stageKey; }
        if (method === 'MILESTONE_BASED') body.milestoneState = milestoneState;
        const r = await axios.post(`${API}/projects/${projectId}/activities/${activityId}/progress/observations`, body);
        toast.success(`Observation created (${r.data.reviewStatus})`);
      }
      onCreated && onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create observation'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="progress-capture-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Camera className="w-5 h-5 text-blue-600" /> Capture Progress</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="progress-capture-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-slate-500">Progress Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="progress-method-select">
              {METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          {method === 'AI_VISUAL_ESTIMATE' && (
            <div>
              <label className="text-xs text-slate-500">Evidence image(s) — AI produces a proposal for your review</label>
              <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} className="w-full mt-1 text-sm" data-testid="progress-ai-files" />
            </div>
          )}
          {method === 'STAGE_BASED' && (
            <>
              <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); setStageKey(''); }} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="progress-template-select">
                <option value="">Choose stage template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {stages.length > 0 && (
                <select value={stageKey} onChange={(e) => setStageKey(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="progress-stage-select">
                  <option value="">Latest complete stage…</option>
                  {stages.map((s) => <option key={s.key} value={s.key}>{s.label} ({s.cumulative}%)</option>)}
                </select>
              )}
            </>
          )}
          {method === 'MILESTONE_BASED' && (
            <select value={milestoneState} onChange={(e) => setMilestoneState(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="progress-milestone-select">
              {MILESTONES.map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
            </select>
          )}
          {method === 'QUANTITY_BASED' && <p className="text-xs text-slate-500">Uses reviewed installed BOQ quantity ÷ planned quantity (Phase 2 reconciliation).</p>}
          {method === 'MANUAL_REVIEW' && <p className="text-xs text-slate-500">Creates an observation an authorized engineer will fill in during review.</p>}

          <p className="text-[11px] text-slate-400">This creates a <b>proposal</b> pending human review — it is not certified progress.</p>
          <button onClick={submit} disabled={busy || (method === 'AI_VISUAL_ESTIMATE' && files.length === 0)} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="progress-capture-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Submit for Review
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgressCaptureModal;
